import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { fmtDate } from "@/lib/constants";
import { Plus, X, Trash2, Pencil, Truck, Printer, Download, Search, Package } from "lucide-react";
import { BulkBar, RowCheckbox, bulkDelete } from "@/components/BulkBar";

const STATUS_STYLES = {
  draft:      "bg-slate-500/10 text-slate-500 border-slate-500/30",
  dispatched: "bg-blue-500/10 text-blue-500 border-blue-500/30",
  received:   "bg-emerald-500/10 text-emerald-500 border-emerald-500/30",
  cancelled:  "bg-rose-500/10 text-rose-600 border-rose-500/30",
};

const today = () => new Date().toISOString().slice(0, 10);

export default function DeliveryChallans() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [date, setDate] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (date) params.set("date", date);
      if (status) params.set("status", status);
      const [r, c, e] = await Promise.all([
        api.get(`/delivery-challans${params.toString() ? `?${params}` : ""}`),
        api.get("/customers"),
        api.get("/users/engineers"),
      ]);
      setItems(r.data); setCustomers(c.data); setEngineers(e.data);
    } catch (er) { setErr(formatApiError(er)); }
  };
  useEffect(() => { load(); }, [date, status]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const s = q.toLowerCase();
    return items.filter((x) =>
      (x.challan_no || "").toLowerCase().includes(s) ||
      (x.customer_name || "").toLowerCase().includes(s) ||
      (x.engineer_name || "").toLowerCase().includes(s) ||
      (x.reference_no || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  const downloadPdf = async (id, no) => {
    try {
      const r = await api.get(`/delivery-challans/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${no || "challan"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(formatApiError(e)); }
  };

  const printPdf = async (id) => {
    try {
      const r = await api.get(`/delivery-challans/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const w = window.open(url, "_blank");
      if (w) w.addEventListener("load", () => setTimeout(() => w.print(), 400));
    } catch (e) { setErr(formatApiError(e)); }
  };

  const ids = filtered.map((x) => x.id);
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  const someSelected = ids.some((id) => selected.has(id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) ids.forEach((id) => n.delete(id));
    else ids.forEach((id) => n.add(id));
    return n;
  });
  const clearSel = () => setSelected(new Set());
  const doBulkDelete = async () => {
    const arr = Array.from(selected);
    if (!window.confirm(`Delete ${arr.length} challan(s)?`)) return;
    setBulkBusy(true);
    const ok = await bulkDelete(arr, (id) => api.delete(`/delivery-challans/${id}`));
    setBulkBusy(false); clearSel(); await load();
    if (ok < arr.length) setErr(`${arr.length - ok} deletion(s) failed.`);
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-eyebrow text-primary">Logistics</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Delivery Challan</h1>
          <p className="mt-1 text-sm text-muted-foreground">Goods dispatch notes with engineer sign-off, download and print.</p>
        </div>
        <button
          data-testid="dc-create-open"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-3.5 w-3.5" /> New challan
        </button>
      </header>

      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            data-testid="dc-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search challan #, customer, engineer, ref…"
            className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm"
          />
        </div>
        <input data-testid="dc-date-filter" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-md border border-input bg-background px-3 py-2 text-sm" />
        <select data-testid="dc-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-input bg-background px-3 py-2 text-sm">
          <option value="">All status</option>
          <option value="draft">Draft</option>
          <option value="dispatched">Dispatched</option>
          <option value="received">Received</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="sharp-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-secondary/30 text-left">
              <tr>
                <th className="w-10 px-4 py-2"><RowCheckbox testid="dc-select-all" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} /></th>
                <th className="px-4 py-2 label-eyebrow">Challan #</th>
                <th className="px-4 py-2 label-eyebrow">Date</th>
                <th className="px-4 py-2 label-eyebrow">Customer</th>
                <th className="px-4 py-2 label-eyebrow">Engineer</th>
                <th className="px-4 py-2 label-eyebrow">Items</th>
                <th className="px-4 py-2 label-eyebrow">Ref/PO</th>
                <th className="px-4 py-2 label-eyebrow">Status</th>
                <th className="px-4 py-2 label-eyebrow">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} data-testid={`dc-row-${c.id}`} className="border-b border-border/40 hover:bg-secondary/40">
                  <td className="px-4 py-2"><RowCheckbox testid={`dc-select-${c.id}`} checked={selected.has(c.id)} onChange={() => toggleOne(c.id)} /></td>
                  <td className="px-4 py-2 font-mono text-xs font-semibold text-primary">{c.challan_no}</td>
                  <td className="px-4 py-2 text-muted-foreground">{fmtDate(c.challan_date)}</td>
                  <td className="px-4 py-2">{c.customer_name || "—"}</td>
                  <td className="px-4 py-2 text-muted-foreground">{c.engineer_name || "—"}</td>
                  <td className="px-4 py-2 font-semibold">{c.item_count} <span className="text-[10px] font-normal text-muted-foreground">({c.total_qty} qty)</span></td>
                  <td className="px-4 py-2 font-mono text-[11px]">{c.reference_no || "—"}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${STATUS_STYLES[c.status] || STATUS_STYLES.draft}`}>{c.status}</span>
                    {c.stock_deducted && (
                      <span data-testid={`dc-stock-deducted-${c.id}`} title="Parts deducted from Spare Master stock" className="ml-1 inline-flex rounded-md bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald-600">Stock −</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      <button data-testid={`dc-print-${c.id}`} onClick={() => printPdf(c.id)} title="Print" className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Printer className="h-3.5 w-3.5" /></button>
                      <button data-testid={`dc-download-${c.id}`} onClick={() => downloadPdf(c.id, c.challan_no)} title="Download PDF" className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Download className="h-3.5 w-3.5" /></button>
                      <button data-testid={`dc-edit-${c.id}`} onClick={() => setEditing(c)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
                      <button
                        data-testid={`dc-delete-${c.id}`}
                        onClick={async () => { if (!window.confirm(`Delete ${c.challan_no}?`)) return; try { await api.delete(`/delivery-challans/${c.id}`); await load(); } catch (e) { setErr(formatApiError(e)); } }}
                        className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-xs text-muted-foreground">No delivery challans.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <BulkBar count={selected.size} entityLabel="challans" onClear={clearSel} onDelete={doBulkDelete} busy={bulkBusy} />

      {creating && <ChallanModal customers={customers} engineers={engineers} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <ChallanModal existing={editing} customers={customers} engineers={engineers} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ChallanModal({ existing, customers, engineers, onClose, onSaved }) {
  const [form, setForm] = useState({
    challan_date: existing?.challan_date || today(),
    customer_id: existing?.customer_id || "",
    engineer_id: existing?.engineer_id || "",
    engineer_name: existing?.engineer_name || "",
    delivered_to: existing?.delivered_to || "",
    receiver_phone: existing?.receiver_phone || "",
    vehicle_no: existing?.vehicle_no || "",
    driver_name: existing?.driver_name || "",
    reference_no: existing?.reference_no || "",
    notes: existing?.notes || "",
    status: existing?.status || "draft",
  });
  const [items, setItems] = useState(existing?.items?.length ? existing.items.map((it) => ({ ...it })) : [{ part_name: "", part_number: "", brand: "", quantity: 1, unit: "pcs", remarks: "" }]);
  const [spares, setSpares] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/spares").then((r) => setSpares(r.data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const updateItem = (i, patch) => setItems((rows) => rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = () => setItems([...items, { part_name: "", part_number: "", brand: "", quantity: 1, unit: "pcs", remarks: "" }]);
  const removeRow = (i) => setItems(items.length > 1 ? items.filter((_, idx) => idx !== i) : items);
  const pickSpare = (i, spareId) => {
    if (!spareId) return updateItem(i, { spare_id: null });
    const s = spares.find((x) => x.id === spareId);
    if (!s) return;
    updateItem(i, { spare_id: s.id, part_name: s.part_name, part_number: s.part_number || "", brand: s.brand || "" });
  };

  // Auto-fill engineer_name when a stored id is picked
  const pickEngineer = (id) => {
    const e = engineers.find((x) => x.id === id);
    setForm({ ...form, engineer_id: id, engineer_name: e?.name || "" });
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const cleanItems = items
        .filter((it) => (it.part_name || "").trim())
        .map((it) => ({
          part_name: it.part_name.trim(),
          part_number: (it.part_number || "").trim() || null,
          brand: (it.brand || "").trim() || null,
          quantity: Math.max(1, Number(it.quantity) || 1),
          unit: it.unit || "pcs",
          remarks: (it.remarks || "").trim() || null,
          spare_id: it.spare_id || null,
        }));
      if (cleanItems.length === 0) throw new Error("Add at least one item with a part name.");
      const payload = { ...form, items: cleanItems };

      // Low-stock warning before dispatching
      const willDeduct = ["dispatched", "received"].includes(payload.status) && !(existing && existing.stock_deducted);
      if (willDeduct) {
        const pv = await api.post("/delivery-challans/stock-preview", { items: cleanItems });
        if (pv.data.has_warnings) {
          const lines = pv.data.rows
            .filter((r) => r.below_reorder || r.insufficient)
            .map((r) => r.insufficient
              ? `• ${r.part_name}: only ${r.current_stock} in stock, need ${r.quantity}`
              : `• ${r.part_name}: stock will drop to ${r.new_stock} (reorder level ${r.reorder_level})`)
            .join("\n");
          if (!window.confirm(`LOW STOCK WARNING\n\n${lines}\n\nDispatch anyway and deduct stock?`)) {
            setBusy(false);
            return;
          }
        }
      }

      if (existing) await api.patch(`/delivery-challans/${existing.id}`, payload);
      else await api.post("/delivery-challans", payload);
      onSaved();
    } catch (e) { setErr(e.response ? formatApiError(e) : e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-4xl sharp-card max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border/60 bg-card px-5 py-3">
          <div>
            <p className="label-eyebrow text-primary">{existing ? existing.challan_no : "New delivery challan"}</p>
            <h3 className="mt-1 font-heading text-lg font-semibold flex items-center gap-2"><Truck className="h-5 w-5" /> {existing ? `Edit — ${existing.customer_name || ""}` : "Create delivery challan"}</h3>
          </div>
          <button data-testid="dc-modal-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid gap-3 sm:grid-cols-4">
            <div>
              <label className="label-eyebrow mb-1.5 block">Date *</label>
              <input data-testid="dc-form-date" type="date" required value={form.challan_date} onChange={set("challan_date")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow mb-1.5 block">Customer *</label>
              <select data-testid="dc-form-customer" required value={form.customer_id} onChange={set("customer_id")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">Select customer…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Status</label>
              <select data-testid="dc-form-status" value={form.status} onChange={set("status")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="draft">Draft</option>
                <option value="dispatched">Dispatched</option>
                <option value="received">Received</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow mb-1.5 block">Engineer</label>
              <select data-testid="dc-form-engineer" value={form.engineer_id} onChange={(e) => pickEngineer(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">— Not assigned —</option>
                {engineers.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow mb-1.5 block">Ref / PO No</label>
              <input data-testid="dc-form-ref" value={form.reference_no} onChange={set("reference_no")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="PO-123, TKT-000045" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow mb-1.5 block">Delivered to</label>
              <input data-testid="dc-form-receiver" value={form.delivered_to} onChange={set("delivered_to")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Receiver name at customer site" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow mb-1.5 block">Receiver phone</label>
              <input data-testid="dc-form-receiver-phone" value={form.receiver_phone} onChange={set("receiver_phone")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow mb-1.5 block">Vehicle no</label>
              <input data-testid="dc-form-vehicle" value={form.vehicle_no} onChange={set("vehicle_no")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow mb-1.5 block">Driver name</label>
              <input data-testid="dc-form-driver" value={form.driver_name} onChange={set("driver_name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="label-eyebrow flex items-center gap-1"><Package className="h-3 w-3" /> Items</p>
              <button type="button" data-testid="dc-item-add" onClick={addRow} className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-[11px] font-semibold hover:bg-secondary">
                <Plus className="h-3 w-3" /> Add row
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="rounded-md border border-border/60 p-3">
                  <div className="grid gap-2 sm:grid-cols-12">
                    <div className="sm:col-span-3">
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">From Spare Master</label>
                      <select data-testid={`dc-item-spare-${i}`} value={it.spare_id || ""} onChange={(e) => pickSpare(i, e.target.value)} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs">
                        <option value="">— Manual —</option>
                        {spares.map((s) => <option key={s.id} value={s.id}>{s.part_id} · {s.part_name}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-3">
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Part name *</label>
                      <input data-testid={`dc-item-name-${i}`} required value={it.part_name} onChange={(e) => updateItem(i, { part_name: e.target.value })} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Part No</label>
                      <input data-testid={`dc-item-partno-${i}`} value={it.part_number || ""} onChange={(e) => updateItem(i, { part_number: e.target.value })} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                    </div>
                    <div className="sm:col-span-1">
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Qty</label>
                      <input data-testid={`dc-item-qty-${i}`} type="number" min="1" value={it.quantity} onChange={(e) => updateItem(i, { quantity: e.target.value })} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                    </div>
                    <div className="sm:col-span-1">
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Unit</label>
                      <input value={it.unit || "pcs"} onChange={(e) => updateItem(i, { unit: e.target.value })} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                    </div>
                    <div className="sm:col-span-1 flex items-end">
                      <button type="button" data-testid={`dc-item-remove-${i}`} onClick={() => removeRow(i)} className="w-full rounded-md border border-border/70 px-2 py-1.5 text-muted-foreground hover:text-destructive">
                        <Trash2 className="mx-auto h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="sm:col-span-12">
                      <input placeholder="Remarks (optional)" value={it.remarks || ""} onChange={(e) => updateItem(i, { remarks: e.target.value })} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="label-eyebrow mb-1.5 block">Notes</label>
            <textarea data-testid="dc-form-notes" rows={2} value={form.notes} onChange={set("notes")} className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>

          {err && <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="dc-form-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : (existing ? "Save changes" : "Create challan")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
